import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";

import { useI18n } from "../i18n/I18nProvider.js";
import { determineContentAvailability } from "./contentState.js";
import type { ContentAvailabilityState } from "./contentState.js";

interface ContentStatusContextValue {
  readonly refresh: () => Promise<void>;
  readonly setUpdating: (updating: boolean) => void;
  readonly state: ContentAvailabilityState;
}

const ContentStatusContext = createContext<ContentStatusContextValue | null>(null);

export function ContentStatusProvider({ children }: PropsWithChildren) {
  const { locale } = useI18n();
  const [state, setState] = useState<ContentAvailabilityState>("NEVER_DOWNLOADED");

  const refresh = async () => {
    setState(await determineContentAvailability(locale));
  };

  useEffect(() => {
    void refresh();
    const handleChange = () => void refresh();
    window.addEventListener("safesphere:content-state-changed", handleChange);
    return () => window.removeEventListener("safesphere:content-state-changed", handleChange);
  }, [locale]);

  const value = useMemo<ContentStatusContextValue>(
    () => ({
      refresh,
      setUpdating: (updating) => {
        if (updating) setState("UPDATING");
        else void refresh();
      },
      state,
    }),
    [locale, state],
  );

  return <ContentStatusContext.Provider value={value}>{children}</ContentStatusContext.Provider>;
}

export function useContentStatus(): ContentStatusContextValue {
  const context = useContext(ContentStatusContext);
  if (context === null)
    throw new Error("useContentStatus must be used inside ContentStatusProvider.");
  return context;
}
